import React, {useState} from 'react';
import {useParams, Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import WorkEntryCard from '../components/WorkEntryCard';
import * as FiIcons from 'react-icons/fi';

const {FiGithub, FiTwitter, FiClock, FiZap, FiActivity} = FiIcons;

const glassStyle = "bg-white/5 border border-white/10 backdrop-blur-md shadow-2xl rounded-[2rem]";

function Page() {
  const {username} = useParams();

  const user = {
    username: 'alexdoe',
    name: 'Alex Doe',
    bio: 'Building tools for makers. Currently working on productivity apps and developer workflows.',
    links: [
      {type: 'github', url: 'https://github.com/alexdoe', label: 'GitHub'},
      {type: 'twitter', url: 'https://twitter.com/alexdoe', label: 'Twitter'}
    ],
    stats: {
      'Total Hours': '1,240h',
      'Streak': '12 Days',
      'Artifacts': '482'
    }
  };

  const workEntries = [
    {
      id: '2',
      title: 'Shipped dashboard redesign',
      summary: 'New layout is live. Reduced cognitive load and improved navigation patterns. Focused on making the core flow feel instantaneous.',
      timestamp: '2024-01-19T16:45:00Z',
      duration: '5h 30m',
      visibility: 'public',
      artifacts: [{type: 'design', name: 'dashboard-v2.figma', size: '4.2mb'}]
    },
    {
      id: '3',
      title: 'Database optimization',
      summary: 'Identified and fixed slow queries in the reporting module. Implemented a new caching layer that improved response times by 40%.',
      timestamp: '2024-01-18T09:00:00Z',
      duration: '4h 20m',
      visibility: 'public'
    }
  ];

  const provenanceLinks = [
    {tool: 'React', url: 'https://react.dev'},
    {tool: 'Vite', url: 'https://vitejs.dev'},
    {tool: 'Tailwind', url: 'https://tailwindcss.com'}
  ];

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      className="min-h-screen bg-slate-950 pt-32 pb-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Profile Sidebar */}
          <div className="lg:col-span-4 space-y-8">
            <div className={`${glassStyle} p-10 text-center lg:text-left relative overflow-hidden`}>
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full" />
              
              <div className="relative z-10">
                <div className="w-24 h-24 bg-gradient-to-tr from-indigo-500 to-blue-600 rounded-[2rem] mx-auto lg:mx-0 mb-8 flex items-center justify-center text-white text-4xl font-black shadow-2xl shadow-indigo-500/20">
                  AD
                </div>
                
                <h1 className="text-3xl font-black text-white mb-2 tracking-tighter italic uppercase">
                  {user.name}
                </h1>
                
                <p className="text-slate-400 mb-8 font-medium leading-relaxed">
                  {user.bio}
                </p>
                
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-10">
                  {user.links.map(link => (
                    <a
                      key={link.type}
                      href={link.url}
                      className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all text-slate-300 hover:text-white"
                    >
                      <SafeIcon icon={link.type === 'github' ? FiGithub : FiTwitter} className="w-5 h-5" />
                    </a>
                  ))}
                  
                  <button className="flex-1 lg:flex-none px-8 py-3 bg-white text-slate-950 rounded-xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-transform">
                    Follow
                  </button>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(user.stats).map(([key, val]) => (
                    <div key={key} className="bg-white/5 p-4 rounded-2xl text-center border border-white/5">
                      <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 mb-1">{key}</p>
                      <p className="text-lg font-black text-white tracking-tighter">{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Built With Section */}
            <div className={`${glassStyle} p-8`}>
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-6">Built With</h3>
              <div className="flex flex-wrap gap-2">
                {provenanceLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:border-white/20 transition-colors"
                  >
                    <span>{link.tool}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Main Timeline */}
          <div className="lg:col-span-8 space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em]">Timeline</h2>
              <span className="text-xs font-bold text-slate-600">{workEntries.length} entries</span>
            </div>

            <div className="space-y-6">
              {workEntries.length === 0 ? (
                <div className={`${glassStyle} p-20 text-center`}>
                  <SafeIcon icon={FiActivity} className="w-12 h-12 text-slate-700 mx-auto mb-6" />
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-4">
                    Nothing here yet. Ready when you are.
                  </h3>
                  <p className="text-slate-500 font-medium">
                    Work entries will appear here as they're documented.
                  </p>
                </div>
              ) : (
                workEntries.map(entry => (
                  <WorkEntryCard key={entry.id} entry={entry} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Page;